import React, { useEffect, useState } from "react";
import { Radar, Satellite, Check, X, Loader2, RefreshCw, Sparkles, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const IMPACT = {
  HIGH: "text-rose-300 bg-rose-400/10 border-rose-400/30",
  NOTABLE: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  ROUTINE: "text-slate-400 bg-white/5 border-white/10",
};
const CATEGORY_TINT = {
  model: "text-cyan-300", robotics: "text-orange-300", embodied: "text-orange-200",
  hardware: "text-emerald-300", agents: "text-sky-300", research: "text-purple-300",
  market: "text-yellow-300", generative: "text-pink-300", opensource: "text-teal-300", safety: "text-red-300",
};

function Chip({ children, className = "" }) {
  return <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono-data uppercase tracking-wider border ${className}`}>{children}</span>;
}

export default function AIFrontierRadar() {
  const [data, setData] = useState(null);
  const [discoveries, setDiscoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/brain/radar"),
        api.get("/brain/radar/discoveries", { params: { limit: 50 } }),
      ]);
      setData(s.data);
      setDiscoveries(d.data.discoveries || []);
    } catch {
      toast.error("Could not reach the AI Frontier Radar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      await api.post("/brain/radar/scan");
      toast.success("Radar sweep running — new discoveries arrive in ~1–2 min. Auto-refreshing.");
      setTimeout(load, 60000);
      setTimeout(load, 100000);
    } catch { toast.error("Scan failed to start."); }
    finally { setTimeout(() => setScanning(false), 3000); }
  };

  const act = async (did, action) => {
    setBusyId(did);
    try {
      const res = await api.post(`/brain/radar/discoveries/${did}/${action}`);
      if (action === "approve" && res.data?.ok) toast.success("Embedded into AVA's brain — now known fleet-wide.");
      else if (action === "approve") toast.warning(res.data?.message || "Nothing to embed.");
      else toast.message("Discovery dismissed.");
      await load();
    } catch { toast.error("Action failed."); }
    finally { setBusyId(null); }
  };

  const pending = discoveries.filter((d) => d.status === "pending");
  const history = discoveries.filter((d) => d.status !== "pending");

  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl p-4 sm:p-6 overflow-y-auto" data-testid="radar-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center shrink-0">
            <Radar className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <h2 className="font-display font-bold text-white text-lg leading-tight">AI Frontier Radar</h2>
            <p className="text-slate-400 text-xs mt-1 max-w-2xl">
              AVA's outward eye on the <span className="text-emerald-300">real world of AI</span> — new frontier models, humanoid robotics, embodied AI, compute, agents, breakthrough papers and market moves. It proposes each genuinely new development for your approval; only what you approve is <span className="text-emerald-300">embedded into the brain</span> and shared with the whole fleet.
            </p>
          </div>
        </div>
        <button
          data-testid="radar-scan-button"
          onClick={runScan}
          disabled={scanning}
          className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25 border border-emerald-400/30 text-[11px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Satellite className="w-3.5 h-3.5" />}
          {scanning ? "Sweeping…" : "Run Radar Sweep"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning the frontier…</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6" data-testid="radar-stats">
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Known (Embedded)</div>
              <div className="text-2xl font-display font-bold text-emerald-300">{data?.knowledge_count ?? 0}</div>
            </div>
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Pending Approval</div>
              <div className="text-2xl font-display font-bold text-amber-300">{data?.pending_count ?? 0}</div>
            </div>
            <div className="glass-panel rounded-xl p-3 border border-white/5 col-span-2">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mb-1.5">Coverage</div>
              <div className="flex flex-wrap gap-1.5">
                {["Models","Robotics","Embodied","Hardware","Agents","Research","Market","Generative","Open-source","Safety"].map((c) => (
                  <Chip key={c} className="border-white/10 text-slate-400">{c}</Chip>
                ))}
              </div>
            </div>
          </div>

          {/* Pending discoveries — one-tap approval */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <h3 className="font-display font-semibold text-white text-sm">New Discoveries</h3>
              <span className="text-[10px] font-mono-data text-slate-500">awaiting your approval to embed</span>
            </div>
            {pending.length === 0 ? (
              <p className="text-slate-500 text-xs italic" data-testid="radar-no-pending">No new discoveries pending. Run a radar sweep to have AVA scan the latest AI + robotics frontier.</p>
            ) : (
              <div className="space-y-3" data-testid="radar-pending-list">
                {pending.map((d) => (
                  <div key={d.did} data-testid={`radar-discovery-${d.did}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Chip className={IMPACT[d.impact] || IMPACT.NOTABLE}>{d.impact}</Chip>
                        <Chip className={`border-white/10 ${CATEGORY_TINT[d.category] || "text-slate-300"}`}>{d.category}</Chip>
                        <span className="text-white text-sm font-display font-semibold">{d.headline}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`approve-discovery-${d.did}`}
                          onClick={() => act(d.did, "approve")}
                          disabled={busyId === d.did}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25 border border-emerald-400/30 text-[10px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                          {busyId === d.did ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                        </button>
                        <button
                          data-testid={`reject-discovery-${d.did}`}
                          onClick={() => act(d.did, "reject")}
                          disabled={busyId === d.did}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10 text-[10px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                          <X className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                    <p className="text-slate-300 text-xs leading-relaxed">{d.what}</p>
                    {d.why && <p className="text-emerald-200/80 text-[11px] mt-1.5"><span className="text-slate-500 font-mono-data uppercase text-[9px] mr-1">Why it matters</span>{d.why}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {d.model && <span className="text-slate-600 text-[10px] font-mono-data">via {d.model}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Known knowledge (approved, fleet-wide) */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-emerald-300" />
              <h3 className="font-display font-semibold text-white text-sm">Embedded Knowledge</h3>
              <span className="text-[10px] font-mono-data text-slate-500">known fleet-wide</span>
            </div>
            {(data?.recent_knowledge || []).length === 0 ? (
              <p className="text-slate-500 text-xs italic">Nothing embedded yet. Approve a discovery above to teach it to AVA's brain.</p>
            ) : (
              <ul className="space-y-2" data-testid="radar-knowledge-list">
                {data.recent_knowledge.map((k, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300 rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                    <span className="text-emerald-400 mt-0.5">◆</span>
                    <div>
                      <span className="text-slate-200">{k.headline}</span>
                      <span className="text-slate-600 text-[10px] ml-2 font-mono-data">{(k.category || "").toUpperCase()}{k.model ? ` · ${k.model}` : ""}</span>
                      {k.why && <p className="text-slate-500 text-[11px] mt-0.5">{k.why}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <details className="mt-4">
              <summary className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 cursor-pointer flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" /> Reviewed discoveries ({history.length})
              </summary>
              <div className="mt-2 space-y-1.5">
                {history.map((d) => (
                  <div key={d.did} className="flex items-center gap-2 text-[11px] text-slate-500 py-1">
                    <Chip className={d.status === "approved" ? IMPACT.HIGH : IMPACT.ROUTINE}>{d.status}</Chip>
                    <span className="text-slate-400">{d.headline}</span>
                    <span className={`text-[9px] ${CATEGORY_TINT[d.category] || ""}`}>{d.category}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
